import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Approval action bar — drop in on any PO / Invoice detail or view modal.
 *
 * Props:
 *  - docKind   — 'purchase_order' | 'sales_invoice'
 *  - docId     — PO uuid, or Sales Invoice number (already URL-safe)
 *  - status    — current approval_status ('draft' | 'pending_l1' | 'pending_l2' | 'approved')
 *  - lastRejectionReason — optional string shown above the buttons
 *  - onChange  — () => void; called after submit/approve/reject so the parent
 *                can refresh the doc.
 */
export default function ApprovalBar({
  docKind,
  docId,
  status = 'draft',
  lastRejectionReason,
  onChange,
}) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const basePath =
    docKind === 'purchase_order'
      ? `/purchase-orders/${docId}`
      : `/sales/invoices/${encodeURIComponent(docId)}`;

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/approval-settings').catch(() => ({ data: null })),
      api.get(`${basePath}/approval-history`).catch(() => ({ data: { history: [] } })),
    ]).then(([s, h]) => {
      if (cancelled) return;
      setSettings(s.data);
      setHistory(h.data?.history || []);
    });
    return () => { cancelled = true; };
    // re-fetch whenever the status or doc changes
  }, [basePath, status]);

  const isCreator = settings && false; // (we don't expose creator on docs uniformly; allow self-approval per spec)

  const inL1Pool =
    docKind === 'purchase_order'
      ? (settings?.po_l1_approver_ids || []).includes(user?.id)
      : (settings?.invoice_l1_approver_ids || []).includes(user?.id);
  const inL2Pool =
    docKind === 'purchase_order'
      ? (settings?.po_l2_approver_ids || []).includes(user?.id)
      : (settings?.invoice_l2_approver_ids || []).includes(user?.id);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`${basePath}/submit-for-approval`);
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.post(`${basePath}/approve`);
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  const handleReject = async () => {
    const reason = window.prompt('Reason for rejection (required):');
    if (!reason || !reason.trim()) return;
    setLoading(true);
    try {
      await api.post(`${basePath}/reject`, { reason: reason.trim() });
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const statusMeta = {
    draft: { label: 'DRAFT', color: 'bg-amber-100 text-amber-800 border-amber-300' },
    pending_l1: { label: 'PENDING — Level 1', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    pending_l2: { label: 'PENDING — Level 2', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    approved: { label: '✓ APPROVED', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  }[status] || { label: status, color: 'bg-gray-100 text-gray-700 border-gray-300' };

  const canApproveNow =
    (status === 'pending_l1' && inL1Pool) ||
    (status === 'pending_l2' && inL2Pool);

  return (
    <div className="border rounded p-3 bg-gray-50 mb-3" data-testid={`approval-bar-${docKind}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600">Approval Status:</span>
          <span
            className={`text-xs px-2 py-1 rounded border font-bold ${statusMeta.color}`}
            data-testid="approval-status-badge"
          >
            {statusMeta.label}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status === 'draft' && (
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
              data-testid="approval-submit-btn"
            >
              📤 Submit for Approval
            </button>
          )}
          {canApproveNow && (
            <>
              <button
                type="button"
                disabled={loading}
                onClick={handleApprove}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm disabled:opacity-50"
                data-testid="approval-approve-btn"
              >
                ✓ Approve (Level {status === 'pending_l1' ? 1 : 2})
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleReject}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm disabled:opacity-50"
                data-testid="approval-reject-btn"
              >
                ✕ Reject
              </button>
            </>
          )}
          {(status === 'pending_l1' || status === 'pending_l2') && !canApproveNow && (
            <span className="text-xs text-gray-500 italic">
              Awaiting{' '}
              {status === 'pending_l1' ? 'Level-1' : 'Level-2'} approver
              {isAdmin ? ' (you are not in this level\'s pool)' : ''}
            </span>
          )}
          {history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="text-xs text-blue-700 hover:underline"
            >
              {showHistory ? 'Hide' : 'View'} history ({history.length})
            </button>
          )}
        </div>
      </div>

      {status === 'draft' && lastRejectionReason && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-900">
          <b>Last rejected:</b> {lastRejectionReason}
        </div>
      )}

      {showHistory && (
        <div className="mt-3 max-h-48 overflow-y-auto border rounded bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left">When</th>
                <th className="px-2 py-1 text-left">Action</th>
                <th className="px-2 py-1 text-left">By</th>
                <th className="px-2 py-1 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              {[...history].reverse().map((h, i) => (
                <tr key={`h-${i}`} className="border-t">
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(h.at).toLocaleString()}</td>
                  <td className="px-2 py-1 uppercase">
                    {h.action}{h.level ? ` L${h.level}` : ''}
                  </td>
                  <td className="px-2 py-1">{h.user_name}</td>
                  <td className="px-2 py-1 text-red-700">{h.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
